// src/components/Navbar.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { trackEvent } from '../App';
import '../assets/components/_navbar.scss';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();

  // Handle scroll events with throttling
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;

    // Set scrolled state
    setIsScrolled(currentScrollY > 50);

    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
   
    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsMenuOpen(false);
    }
  };

  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const handleNavClick = (path, label) => {
    setIsMenuOpen(false);
    trackEvent('Navigation', 'Click', label);
  };

  const handleMenuToggle = () => {
    const newMenuState = !isMenuOpen;
    setIsMenuOpen(newMenuState);
    trackEvent('Navigation', 'Menu Toggle', newMenuState ? 'Open' : 'Close');
  };

  const navItems = [
    { path: '/', label: 'Home'},
    { path: '/projects', label: 'Projects & Experience' },
    { path: '/blog', label: 'Blog' },
    { path: '/testimonials', label: 'Recognition' },
    { path: '/gallery', label: 'Gallery'},
    { path: '/contact', label: 'Contact'},
  ];

  return (
    <nav
      className={`navbar ${isScrolled ? 'scrolled' : ''} ${isMenuOpen ? 'menu-open' : ''} ${!isVisible ? 'hidden' : ''}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="navbar-container">
        <Link
          to="/"
          className="navbar-logo"
          onClick={() => handleNavClick('/', 'Logo')}
          aria-label="Go to homepage"
        >
          Nahom Birhan
        </Link>

        <button
          className={`menu-icon ${isMenuOpen ? 'active' : ''}`}
          onClick={handleMenuToggle}
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-controls="nav-menu"
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </button>

        <ul
          id="nav-menu"
          className={`nav-menu ${isMenuOpen ? 'active' : ''}`}
          role="menubar"
        >
          {navItems.map((item) => (
            <li key={item.path} className="nav-item" role="none">
              <Link
                to={item.path}
                className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleNavClick(item.path, item.label)}
                role="menuitem"
                aria-current={location.pathname === item.path ? 'page' : undefined}
              >
                <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                <span className="nav-text">{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;